import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { clampPercentage, formatPercentage } from '../../../core/utils/common/formatters';
import { sexColours } from '../../../core/constants/colours.constants';
import ManOutlined from '@mui/icons-material/ManOutlined';
import WomanOutlined from '@mui/icons-material/WomanOutlined';
import { RankingItem } from '../../../core/types/common.types';

interface SexIconChartProps {
  items: RankingItem[];
  femaleLabel: string;
  maleLabel: string;
  subText: string;
}

function SexIconChart({ items, maleLabel = 'Male', femaleLabel = 'Female', subText = '' }: SexIconChartProps) {
  const female = items.find((item) => item.label.toLowerCase().includes('female'));
  const male = items.find((item) => item.label.toLowerCase() === 'male');

  const femaleValue = clampPercentage(female?.value || 0);
  const maleValue = clampPercentage(male?.value || 0);

  const totalIcons = 30;
  const femaleIconCount = Math.round((femaleValue / 100) * totalIcons);
  const maleIconCount = totalIcons - femaleIconCount;

  const iconItems = [
    ...Array.from({ length: femaleIconCount }).map((_, index) => ({
      key: `female-${index}`,
      label: femaleLabel,
      Icon: WomanOutlined,
      color: sexColours.female,
    })),
    ...Array.from({ length: maleIconCount }).map((_, index) => ({
      key: `male-${index}`,
      label: maleLabel,
      Icon: ManOutlined,
      color: sexColours.male,
    })),
  ];

  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '160px minmax(0, 1fr) 160px',
          },
          gap: { xs: 2, md: 3 },
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            border: 1,
            borderColor: `${sexColours.female}55`,
            bgcolor: `${sexColours.female}12`,
            textAlign: { xs: 'center', md: 'left' },
          }}
        >
          <Typography
            variant="h3"
            component="p"
            sx={{
              fontWeight: 900,
              color: sexColours.female,
              lineHeight: 1,
            }}
          >
            {formatPercentage(femaleValue)}
          </Typography>

          <Typography
            variant="body2"
            component="p"
            color="text.secondary"
            sx={{
              mt: 0.75,
              fontWeight: 900,
            }}
          >
            {femaleLabel}
          </Typography>
        </Paper>

        <Tooltip
          title={`${femaleLabel} ${formatPercentage(femaleValue)} / ${maleLabel} ${formatPercentage(maleValue)}`}
        >
          <Box
            aria-label={`${femaleLabel} ${formatPercentage(femaleValue)} / ${maleLabel} ${formatPercentage(maleValue)}`}
            sx={{
              width: '100%',
              minHeight: { xs: 190, md: 220 },
              p: { xs: 1.5, md: 2.5 },
              borderRadius: 3,
              bgcolor: 'grey.50',
              border: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(10, minmax(0, 1fr))',
                  sm: 'repeat(15, minmax(0, 1fr))',
                  md: 'repeat(15, minmax(0, 1fr))',
                },
                gap: { xs: 0.35, md: 0.75 },
                width: '100%',
                maxWidth: 680,
              }}
            >
              {iconItems.map(({ key, Icon, color, label }) => (
                <Box
                  key={key}
                  title={label}
                  sx={{
                    color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 0,
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: {
                        xs: 30,
                        sm: 36,
                        md: 44,
                      },
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Tooltip>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 3,
            border: 1,
            borderColor: `${sexColours.male}55`,
            bgcolor: `${sexColours.male}12`,
            textAlign: { xs: 'center', md: 'right' },
          }}
        >
          <Typography
            variant="h3"
            component="p"
            sx={{
              fontWeight: 900,
              color: sexColours.male,
              lineHeight: 1,
            }}
          >
            {formatPercentage(maleValue)}
          </Typography>

          <Typography
            variant="body2"
            component="p"
            color="text.secondary"
            sx={{
              mt: 0.75,
              fontWeight: 900,
            }}
          >
            {maleLabel}
          </Typography>
        </Paper>
      </Box>

      {subText && (
        <Typography
          variant="caption"
          component="p"
          color="text.secondary"
          sx={{
            mt: 1.5,
            fontWeight: 700,
            display: 'block',
            textAlign: 'center',
          }}
        >
          {subText}
        </Typography>
      )}
    </Box>
  );
}

export default SexIconChart;
